import { db, firebaseImport } from '../../config/main_config';
import { waitForCompanyID } from './waitForCompanyID.util';
import { parseDocsMSG } from './parseDocs.msg.util';

/**
 * Interface representing the structure of a single document.
 */
// interface DocumentData {
//     msgPages?: string[];
//     fileName?: string;
//     [key: string]: any; // Additional properties can be added as needed
// }

/**
 * Interface representing the uploaded documents object.
 */
interface UploadedDocs {
    [key: string]: firebaseImport.firestore.DocumentData;
}

/**
 * The `docsByRoute` function fetches documents from Firestore based on a provided message page route
 * and company ID. It can optionally enable real-time updates if the `enableLiveUpdates` flag is set.
 * @param msgPageRoute - Represents the message page route used to filter and retrieve specific documents from Firestore.
 * @param updateCallback - A function that updates the DOM with the new HTML whenever changes occur.
 * @param enableLiveUpdates - A boolean flag that controls whether real-time updates are enabled.
 * @returns - The matching documents or null if none are found (only when live updates are disabled).
 */
export async function docsByRoute(
    msgPageRoute: string = window.location.hash.slice(2),
    updateCallback?: (html: string) => void,
    enableLiveUpdates: boolean = false
): Promise<firebaseImport.firestore.DocumentData[] | null | void> {
    try {
        // Wait for the company ID if needed
        const companyID: string = await waitForCompanyID();

        // Define the path to the Firebase document
        const docRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`);

        // If live updates are enabled, use onSnapshot
        if (enableLiveUpdates && updateCallback) {
            // Use onSnapshot to listen for real-time updates
            docRef.onSnapshot(async (docSnapshot: firebaseImport.firestore.DocumentSnapshot) => {
                if (docSnapshot.exists) {
                    const data = docSnapshot.data() as firebaseImport.firestore.DocumentData;
                    const matchingDocs = filterDocsByRoute(data.uploaded, msgPageRoute);
                    const docListHTML = await parseDocsMSG(matchingDocs);
                    updateCallback(docListHTML);
                } else {
                    console.log('No such document exists!');
                    updateCallback('<li>No matching documents found.</li>');
                }
            });
        } else {
            // Fetch the document once without real-time updates
            const docSnapshot = await docRef.get();

            if (docSnapshot.exists) {
                const data = docSnapshot.data() as firebaseImport.firestore.DocumentData;
                const matchingDocs = filterDocsByRoute(data.uploaded, msgPageRoute);
                return matchingDocs.length > 0 ? matchingDocs : null;
            } else {
                console.log('No such document exists!');
                return null;
            }
        }
    } catch (error) {
        console.error('Error fetching documents:', error);
        throw error;
    }
}

/**
 * Helper function to filter documents by msgPageRoute.
 * @param uploadedDocs - The uploaded documents to filter.
 * @param msgPageRoute - The route used for filtering documents.
 * @returns - An array of matching documents.
 */
function filterDocsByRoute(uploadedDocs: UploadedDocs, msgPageRoute: string): firebaseImport.firestore.DocumentData[] {
    const matchingDocs: firebaseImport.firestore.DocumentData[] = [];
    for (const docKey in uploadedDocs) {
        if (uploadedDocs.hasOwnProperty(docKey)) {
            const document = uploadedDocs[docKey];
            // Check if the document has a "msgPages" array and contains the provided MSG Page Route
            if (document.msgPages && document.msgPages.includes(msgPageRoute)) {
                matchingDocs.push(document);
            }
        }
    }
    return matchingDocs;
}